<script setup lang="ts">
import { computed, watch } from 'vue'
import { useContactForm } from '../../composables/useContactForm'
import { contactReasons } from '../../data/contact'
import Button from '../ui/Button.vue'
import Input from '../ui/Input.vue'

const {
  state,
  clearErrors,
  submitForm,
  resetForm,
  resetSubmissionState,
  messageCharacterCount,
  updateMessageCount,
} = useContactForm()

watch(() => state.form.name, () => clearErrors('name'))
watch(() => state.form.email, () => clearErrors('email'))
watch(() => state.form.subject, () => clearErrors('subject'))
watch(
  () => state.form.message,
  (value) => {
    clearErrors('message')
    updateMessageCount(value)
  }
)

const nearLimit = computed(() => messageCharacterCount.value > 900)

const handleNewMessage = () => {
  resetSubmissionState()
  resetForm()
}
</script>

<template>
  <div class="form-block">
    <!-- ---------- sent ---------- -->
    <div v-if="state.isSubmitted" class="sent">
      <p class="eyebrow eyebrow-rule">Sent</p>
      <h3 class="display display-3">Thanks — that reached me.</h3>
      <p class="prose-body">I reply within a day or two, usually sooner.</p>
      <Button variant="outline" @click="handleNewMessage">Send another</Button>
    </div>

    <!-- ---------- form ---------- -->
    <form v-else class="form" @submit.prevent="submitForm">
      <Input
        v-model="state.form.name"
        label="Name"
        type="text"
        placeholder="Your name"
        :error="state.errors.name"
        :disabled="state.isSubmitting"
        required
      />

      <Input
        v-model="state.form.email"
        label="Email"
        type="email"
        placeholder="you@example.com"
        :error="state.errors.email"
        :disabled="state.isSubmitting"
        required
      />

      <div class="subject">
        <Input
          v-model="state.form.subject"
          label="Subject"
          type="text"
          placeholder="What's this about?"
          :error="state.errors.subject"
          :disabled="state.isSubmitting"
          required
        />
        <div class="reasons">
          <button
            v-for="reason in contactReasons"
            :key="reason"
            type="button"
            class="reason meta"
            :disabled="state.isSubmitting"
            @click="state.form.subject = reason"
          >
            {{ reason }}
          </button>
        </div>
      </div>

      <div class="field">
        <label for="message" class="eyebrow label">
          Message <span aria-hidden="true">*</span>
        </label>
        <textarea
          id="message"
          v-model="state.form.message"
          rows="6"
          placeholder="Context, timeline, what you're trying to build…"
          :disabled="state.isSubmitting"
          :aria-invalid="state.errors.message ? 'true' : undefined"
          :aria-describedby="state.errors.message ? 'message-error' : 'message-count'"
          :class="{ 'has-error': state.errors.message }"
          required
        ></textarea>
        <div class="under">
          <span v-if="state.errors.message" id="message-error" class="error meta" role="alert">
            {{ state.errors.message }}
          </span>
          <span
            id="message-count"
            class="count meta"
            :class="{ 'is-near': nearLimit }"
          >
            {{ messageCharacterCount }}/1000
          </span>
        </div>
      </div>

      <p v-if="state.submitError" class="submit-error meta" role="alert">
        {{ state.submitError }}
      </p>

      <Button type="submit" variant="primary" :loading="state.isSubmitting" full-width>
        {{ state.isSubmitting ? 'Sending…' : 'Send message' }}
      </Button>
    </form>
  </div>
</template>

<style scoped>
.form-block {
  min-width: 0;
}

.form {
  display: grid;
  gap: 1.5rem;
}

.subject {
  display: grid;
  gap: 0.7rem;
}
.reasons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.reason {
  background: none;
  border: 1px solid var(--c-rule);
  border-radius: 3px;
  padding: 0.2rem 0.55rem;
  font-size: 0.7rem;
  color: var(--c-ink-2);
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;
}
.reason:hover:not(:disabled) {
  border-color: var(--c-accent);
  color: var(--c-accent);
}
.reason:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.field {
  display: grid;
  gap: 0.45rem;
}
.label {
  color: var(--c-ink-2);
}

textarea {
  width: 100%;
  background: var(--c-surface);
  color: var(--c-ink);
  border: 1px solid var(--c-rule);
  border-radius: 3px;
  padding: 0.7rem 0.8rem;
  font-family: inherit;
  font-size: var(--step-0);
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.2s ease;
}
textarea::placeholder {
  color: var(--c-ink-3);
}
textarea:focus {
  outline: none;
  border-color: var(--c-accent);
}
textarea:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
textarea.has-error {
  border-color: #b4402a;
}

.under {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}
.count {
  margin-left: auto;
  color: var(--c-ink-3);
}
.count.is-near {
  color: var(--c-warn);
}
.error {
  color: #b4402a;
}

.submit-error {
  padding: 0.7rem 0.85rem;
  border: 1px solid #b4402a;
  border-radius: 3px;
  color: #b4402a;
}

.sent {
  display: grid;
  gap: 1rem;
  justify-items: start;
}
.sent h3 {
  margin: 0;
}
.sent .prose-body {
  margin: 0;
}
</style>
